import React from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import client from './api-client'
import { rescaleLegend } from '../utils/palette'

const PALETTE_RANGES_KEY = 'palette-ranges'
const PALETTE_SCRIPT_KEY = 'palette-script'

/**
 * The logged-in user's custom palette ranges, keyed by farm id and layer name:
 * { 4387: { NDVI_SKYWATCH_30CM: { min: 0.13, max: 0.5, shade: true } } }
 */
export function usePaletteRanges({ enabled = true } = {}) {
	return useQuery(
		PALETTE_RANGES_KEY,
		async () => {
			const data = await client('profile/palette_range/')
			return data.layer_palette_ranges || {}
		},
		{
			enabled,
			staleTime: Infinity,
			refetchOnWindowFocus: false,
		}
	)
}

export function usePaletteRange(farmId, layerName) {
	const enabled = !!farmId && !!layerName
	const { data } = usePaletteRanges({ enabled })
	return (enabled && data?.[farmId]?.[layerName]) || null
}

export function useSavePaletteRange() {
	const queryClient = useQueryClient()
	return useMutation(
		({ farmId, layerName, min, max, shade = false }) =>
			client('profile/palette_range/', {
				method: 'PUT',
				body: { farm: farmId, layer: layerName, min, max, shade },
			}),
		{
			onSuccess: (data) => {
				queryClient.setQueryData(PALETTE_RANGES_KEY, data.layer_palette_ranges || {})
				queryClient.invalidateQueries(PALETTE_SCRIPT_KEY)
			},
		}
	)
}

export function useResetPaletteRange() {
	const queryClient = useQueryClient()
	return useMutation(
		({ farmId, layerName }) =>
			client(
				`profile/palette_range/?farm=${farmId}&layer=${encodeURIComponent(layerName)}`,
				{ method: 'DELETE' }
			),
		{
			onSuccess: (data) => {
				queryClient.setQueryData(PALETTE_RANGES_KEY, data.layer_palette_ranges || {})
				queryClient.invalidateQueries(PALETTE_SCRIPT_KEY)
			},
		}
	)
}

/** What the legend palette control supports for a layer: { adjustable, shading }. */
export function usePaletteOptions(layerKey, { enabled = true } = {}) {
	return useQuery(
		['palette-options', layerKey],
		() => client(`layers/${layerKey}/palette_options/`),
		{
			enabled: enabled && !!layerKey,
			staleTime: Infinity,
			refetchOnWindowFocus: false,
		}
	)
}

/** Adds the palette adjustment's EVALSCRIPTURL (if any) to a parcel image URL. */
export function withPaletteScript(imageUrl, palette) {
	const evalscriptUrl = palette?.evalscript_url
	return evalscriptUrl
		? `${imageUrl}&EVALSCRIPTURL=${encodeURIComponent(evalscriptUrl)}`
		: imageUrl
}

/**
 * The user's saved palette adjustment for a parcel's farm and a layer on `date`, as
 * { evalscript_url, min, max, shade } to add to parcel image requests (comparator, notes,
 * zoning, plant analysis). The layer is given by key or, for saved notes, by name.
 * `indexFilter` also blanks pixels below that index (plant analysis filter). evalscript_url is null
 * when there's no adjustment. With `rangesOnly` only min/max/shade are returned (no script,
 * no date needed), for recolouring legends and vector zones. Errors resolve to the default
 * palette.
 */
export function usePaletteScript(
	{ parcelId, layerKey, layerName, date, rangesOnly = false, indexFilter = null },
	{ enabled = true } = {}
) {
	return useQuery(
		[PALETTE_SCRIPT_KEY, parcelId, layerKey || layerName, rangesOnly ? 'ranges' : date, indexFilter],
		async () => {
			let query = rangesOnly ? 'ranges_only=1' : `date=${encodeURIComponent(date)}`
			if (!rangesOnly && indexFilter != null) query += `&index_filter=${encodeURIComponent(indexFilter)}`
			const endpoint = layerKey
				? `parcels/${parcelId}/layers/${layerKey}/palette_script/?${query}`
				: `parcels/${parcelId}/palette_script/?layer=${encodeURIComponent(layerName)}&${query}`
			try {
				return await client(endpoint)
			} catch (error) {
				return { evalscript_url: null }
			}
		},
		{
			enabled: enabled && !!parcelId && !!(layerKey || layerName) && (rangesOnly || !!date),
			staleTime: Infinity,
			refetchOnWindowFocus: false,
		}
	)
}

/**
 * `legend` rescaled to the user's saved palette range for a parcel's farm and a layer (only
 * in apps that enable palette adjustments), for colouring charts, zones and plants.
 * Returns { legend, hasCustomRange }.
 */
export function usePaletteLegend(parcelId, layer, { enabled = true } = {}) {
	const { data: palette } = usePaletteScript(
		{ parcelId, layerKey: layer?.key, rangesOnly: true },
		{ enabled }
	)
	const hasCustomRange = palette?.min != null && palette?.max != null
	const legend = React.useMemo(
		() => (hasCustomRange ? rescaleLegend(layer?.legend, palette) : layer?.legend),
		[layer, hasCustomRange, palette?.min, palette?.max]
	)
	return { legend, hasCustomRange }
}

/** Suggested { min, max } for a layer from a raw index sample of the farm on `date` (YYYY-MM-DD). */
export function useAutoFitPalette() {
	return useMutation(({ farmId, layerKey, date }) =>
		client(
			`farms/${farmId}/layers/${layerKey}/palette_autofit/?date=${encodeURIComponent(date)}`
		)
	)
}
